import { fabric } from 'fabric';
import { GUIDELINE_COLOR, GUIDELINE_WIDTH } from './constants';

export class Guideline extends fabric.Line {
  constructor(coords, options = {}) {
    const orientation = options.orientation || 'horizontal';

    // Calculate dynamic padding based on canvas size
    const canvas = window.canvas;
    let dynamicPadding = 36; // Default padding

    if (canvas) {
      const canvasWidth = canvas.getWidth();
      const canvasHeight = canvas.getHeight();
      const maxDimension = Math.max(canvasWidth, canvasHeight);

      // Formula: base padding + logarithmic growth based on canvas size
      // For A4 (794x1123px at 96dpi): ~36px padding
      // For A3 (1123x1587px): ~42px padding
      // For A2 (1587x2245px): ~48px padding
      // For A1 (2245x3179px): ~54px padding
      // For A0 (3179x4494px): ~60px padding
      const basePadding = 30;
      const scaleFactor = Math.log10(maxDimension / 500) * 18;
      dynamicPadding = Math.round(basePadding + scaleFactor);

      // Ensure minimum of 36px and maximum of 72px
      dynamicPadding = Math.max(36, Math.min(72, dynamicPadding));
    }

    // Ensure padding is always set unless explicitly provided with a valid value
    const finalOptions = {
      stroke: GUIDELINE_COLOR,
      strokeWidth: GUIDELINE_WIDTH,
      selectable: true,
      hasControls: false,
      hasBorders: false, // Disable borders to remove gray background
      borderColor: 'transparent',
      borderOpacityWhenMoving: 0,
      padding: dynamicPadding, // Dynamic padding for easier selection based on canvas size
      lockMovementX: orientation === 'horizontal', // Horizontal lines can't move left/right
      lockMovementY: orientation === 'vertical', // Vertical lines can't move up/down
      lockRotation: true,
      lockScalingX: true,
      lockScalingY: true,
      orientation: orientation,
      evented: true,
      excludeFromExport: false, // Include in JSON exports
      excludeFromVisualExport: true, // Exclude from visual exports (PNG, etc.)
      hoverCursor: orientation === 'horizontal' ? 'ns-resize' : 'ew-resize',
      moveCursor: orientation === 'horizontal' ? 'ns-resize' : 'ew-resize',
      ...options, // Spread options last to allow overrides
    };

    // Ensure padding is never undefined or too small
    if (!finalOptions.padding || finalOptions.padding < 36) {
      finalOptions.padding = 36;
    }

    super(coords, finalOptions);

    this.type = 'Guideline'; // Set type for proper serialization
    this.isGuideline = true;
    this.excludeFromVisualExport = true; // Ensure this property is always set
    this.customPadding = finalOptions.padding; // Store padding as custom property
    this.padding = finalOptions.padding; // Also set the normal padding
  }

  // Override toObject to ensure all properties are saved
  toObject(propertiesToInclude) {
    return fabric.util.object.extend(super.toObject(propertiesToInclude), {
      isGuideline: this.isGuideline,
      orientation: this.orientation,
      excludeFromExport: this.excludeFromExport,
      excludeFromVisualExport: this.excludeFromVisualExport,
      visible: this.visible,
      padding: this.padding,
      customPadding: this.customPadding,
      type: 'Guideline', // Ensure type is included for proper deserialization
    });
  }
}

// Register Guideline class for proper deserialization
export const registerGuidelineClass = (getState, guidelines) => {
  fabric.Guideline = Guideline;
  fabric.Guideline.fromObject = function (object, callback) {
    // Fix coordinates to ensure guidelines span the full canvas
    let coords;
    const canvas = window.canvas;

    if (object.orientation === 'horizontal') {
      // For horizontal guidelines, ensure they span full width
      coords = [
        0,
        object.top || object.y1 || 0,
        canvas ? canvas.getWidth() : object.x2,
        object.top || object.y2 || 0,
      ];
    } else {
      // For vertical guidelines, ensure they span full height
      coords = [
        object.left || object.x1 || 0,
        0,
        object.left || object.x2 || 0,
        canvas ? canvas.getHeight() : object.y2,
      ];
    }

    const guideline = new Guideline(coords, {
      orientation: object.orientation,
      ...object,
      // Ensure correct positioning
      left:
        object.orientation === 'horizontal' ? 0 : object.left || object.x1 || 0,
      top:
        object.orientation === 'horizontal' ? object.top || object.y1 || 0 : 0,
    });

    // Restore custom padding if it exists
    if (object.customPadding) {
      guideline.customPadding = object.customPadding;
      guideline.padding = object.customPadding;
    }

    // Check if ruler is enabled to determine visibility
    const isRulerEnabled = getState('rulerEnabled', false);
    guideline.set('visible', isRulerEnabled);

    // If ruler is enabled, add to guidelines array
    if (isRulerEnabled && guidelines.indexOf(guideline) === -1) {
      guidelines.push(guideline);
    }

    callback && callback(guideline);
    return guideline;
  };
};
